import { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import toast from "react-hot-toast";
import api from "../utils/api";
// crypto.js exists in utils but encryption is not applied
import useAuthStore from "../store/authStore";
import styles from "./Auth.module.css";

const VerifyOtp = ({ mode = "email" }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login: storeLogin } = useAuthStore();

  const email   = location.state?.email || "";
  const devOtp  = location.state?.devOtp || "";

  const [otp, setOtp] = useState(["","","","","",""]);
  const [loading, setLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(60);
  const inputRefs = useRef([]);

  // Countdown timer
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  // Auto-focus first input
  useEffect(() => { inputRefs.current[0]?.focus(); }, []);

  const handleChange = (i, val) => {
    if (!/^\d*$/.test(val)) return;
    const next = [...otp];
    next[i] = val.slice(-1);
    setOtp(next);
    if (val && i < 5) inputRefs.current[i + 1]?.focus();
    // Auto-submit when all 6 filled
    if (val && i === 5 && next.every(d => d)) {
      handleVerify(next.join(""));
    }
  };

  const handleKeyDown = (i, e) => {
    if (e.key === "Backspace" && !otp[i] && i > 0) {
      inputRefs.current[i - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (pasted.length === 6) {
      const arr = pasted.split("");
      setOtp(arr);
      inputRefs.current[5]?.focus();
      handleVerify(pasted);
    }
  };

  const handleVerify = async (code) => {
    if (loading) return;
    setLoading(true);
    try {
      const endpoint = mode === "2fa" ? "/auth/verify-2fa" : "/auth/verify-email";
      const res = await api.post(endpoint, { email, otp: code });

      if (res.data.success) {
        // Store user in authStore
        useAuthStore.setState({ user: res.data.user, token: res.data.token });
        toast.success(mode === "2fa" ? "Logged in! 🔐" : "Email verified! Welcome to Chatify 🎉");
        navigate("/");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Invalid code");
      setOtp(["","","","","",""]);
      inputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;
    try {
      const endpoint = mode === "2fa" ? "/auth/login" : "/auth/resend-otp";
      const res = await api.post(endpoint, { email });
      toast.success("New code sent to your email");
      setResendCooldown(60);
      if (res.data.devOtp) toast(`Dev mode OTP: ${res.data.devOtp}`, { icon: "🔑" });
    } catch (err) {
      toast.error("Failed to resend code");
    }
  };

  const submitOtp = () => {
    const code = otp.join("");
    if (code.length === 6) handleVerify(code);
  };

  return (
    <div className={styles.authPage}>
      <div className={styles.authCard}>
        <div className={styles.logo}>
          <div className={styles.logoMark}>{mode === "2fa" ? "🔐" : "📧"}</div>
          <h1>Chatify</h1>
        </div>

        <h2 style={{ color: "#e8f1f8", fontSize: 18, fontWeight: 700, marginBottom: 8 }}>
          {mode === "2fa" ? "Two-Factor Authentication" : "Verify your email"}
        </h2>
        <p className={styles.subtitle}>
          We sent a 6-digit code to <strong style={{ color: "#7b7ff5" }}>{email}</strong>
          {devOtp && <><br/><span style={{ color: "#f59e0b", fontSize: 12 }}>Dev mode — OTP: {devOtp}</span></>}
        </p>

        {/* 6-digit OTP input boxes */}
        <div style={{ display: "flex", gap: 10, justifyContent: "center", margin: "28px 0" }}
          onPaste={handlePaste}>
          {otp.map((digit, i) => (
            <input
              key={i}
              ref={el => inputRefs.current[i] = el}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={e => handleChange(i, e.target.value)}
              onKeyDown={e => handleKeyDown(i, e)}
              style={{
                width: 48, height: 56, textAlign: "center",
                fontSize: 22, fontWeight: 800, borderRadius: 10,
                background: "#0e1621",
                border: `2px solid ${digit ? "#5b5bd6" : "#253545"}`,
                color: "#e8f1f8", outline: "none",
                fontFamily: "monospace",
                transition: "border-color 0.15s, box-shadow 0.15s",
                boxShadow: digit ? "0 0 0 3px rgba(91,91,214,0.2)" : "none",
              }}
            />
          ))}
        </div>

        <button
          className={styles.submitBtn}
          onClick={submitOtp}
          disabled={loading || otp.join("").length < 6}
        >
          {loading ? "Verifying…" : mode === "2fa" ? "Verify & Login" : "Verify Email"}
        </button>

        <p style={{ textAlign: "center", marginTop: 20, color: "#5c7a9a", fontSize: 13 }}>
          Didn't receive the code?{" "}
          <button
            onClick={handleResend}
            style={{
              color: resendCooldown > 0 ? "#4d6478" : "#7b7ff5",
              fontWeight: 600, fontSize: 13, cursor: resendCooldown > 0 ? "not-allowed" : "pointer",
              background: "none", border: "none", fontFamily: "inherit",
            }}
            disabled={resendCooldown > 0}
          >
            {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Resend code"}
          </button>
        </p>
      </div>
    </div>
  );
};

export default VerifyOtp;
